import React, { useCallback, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import classnames from 'classnames';
import { NotificationManager } from '../../../../../components/common/react-notifications';
import { AxiosAPI } from '../../../../../helpers/AxiosAPI';
import { aPI_Contacts } from '../../../../../constants/APIs';
// endpoint = existing contacts base URL + /bulk-upload

const ImportCSVModal = ({ visible, toggle, propertyId, groupId, onSuccess, userEmail, userName }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    const resetState = () => {
        setSelectedFile(null);
        setErrorMessage('');
        setIsUploading(false);
    };

    useEffect(() => {
        if (!visible) {
            resetState();
        }
    }, [visible]);

    const handleInvalidFile = useCallback(() => {
        const msg = 'Invalid file type. Please upload a CSV file.';
        setErrorMessage(msg);
        NotificationManager.error(msg, 'Upload Error', 3000);
    }, []);

    const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
        if (rejectedFiles && rejectedFiles.length > 0) {
            handleInvalidFile();
            return;
        }
        if (acceptedFiles && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            const isCsv = file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';
            if (!isCsv) {
                handleInvalidFile();
                return;
            }
            setSelectedFile(
                Object.assign(file, { preview: URL.createObjectURL(file) })
            );
            setErrorMessage('');
        }
    }, [handleInvalidFile]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        multiple: false,
        accept: { 'text/csv': ['.csv'] },
        onDrop,
    });

    const handleSave = async () => {
        if (!selectedFile) {
            handleInvalidFile();
            return;
        }
        if (!propertyId || !groupId) {
            NotificationManager.warning('Select a property before importing.', 'Missing context', 3000);
            return;
        }

        const formData = new FormData();
        formData.append('files', selectedFile, selectedFile.name);
        formData.append('groupId', groupId);
        formData.append('propertyId', propertyId);
        formData.append('Email', userEmail ?? '');
        formData.append('UploadedBy', userName ?? '');

        setIsUploading(true);
        try {
            await AxiosAPI({
                method: 'post',
                url: `${aPI_Contacts}/bulk-upload`,
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            NotificationManager.success('CSV uploaded successfully.', 'Success', 3000);
            if (onSuccess) onSuccess();
            toggle();
        } catch (err) {
            NotificationManager.error('Failed to upload CSV.', 'Error', 3000);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Modal isOpen={visible} toggle={toggle} style={{ maxWidth: '720px' }}>
            <ModalHeader toggle={toggle}>Import CSV</ModalHeader>
            <ModalBody>
                <section className="container">
                    <div
                        {...getRootProps({
                            className: classnames('dropzone', { 'dropzone-active': isDragActive }),
                        })}
                    >
                        <input {...getInputProps()} />
                        <div className="dz-default dz-message">
                            <span>
                                Drop CSV file here to upload, or click to select a file. Only .csv format is supported.
                            </span>
                            <p className="font-weight-medium mb-0">
                                <i className="demo-icon simple-icon-cursor-move" />
                            </p>
                        </div>
                    </div>
                    <div className="filepicker dropzone-gallery dz-clickable dz-started" style={{ minHeight: '100px', border: '0px' }}>
                        {selectedFile && (
                            <div className="dz-preview dz-file-preview mb-3" style={{ height: '100px' }}>
                                <div className="row">
                                    <div className="pl-3 pt-2 pr-2 pb-1 w-100 dz-details position-relative">
                                        <div><span data-dz-name>{selectedFile.name}</span></div>
                                        <div className="text-primary text-extra-small" data-dz-size>
                                            {(selectedFile.size / 1024).toFixed(2)} KB
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {errorMessage && <p className="text-danger mt-2">{errorMessage}</p>}
                </section>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" size="xs" onClick={handleSave} disabled={isUploading}>
                    Save
                </Button>
                <Button color="secondary" size="xs" onClick={toggle} disabled={isUploading}>
                    Back
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ImportCSVModal;